<template>
  <div class="gid-global-feed">
    <!-- Header -->
    <div
      class="d-flex flex-wrap justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-2 mb-md-0">
        {{ $t('global-activity-feed.title') }}
      </h5>
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="loadData"
        :disabled="loading"
      >
        <b-spinner v-if="loading" small class="mr-1"></b-spinner>
        <b-icon-arrow-clockwise v-else class="mr-1"></b-icon-arrow-clockwise>
        <span class="d-none d-sm-inline">
          {{ $t('global-activity-feed.refresh') }}
        </span>
      </b-button>
    </div>

    <!-- Filters -->
    <div v-if="!loading" class="mb-3 d-flex flex-wrap">
      <div
        v-for="(type, key) in types"
        :key="key"
        class="mr-2 mb-1"
        @click.alt.stop.prevent="() => checkOnly(key)"
      >
        <b-form-checkbox
          inline
          v-model="type.enabled"
          :disabled="type.loading"
          class="gid-global-feed__filter"
        >
          <small>
            <component :is="type.icon" />
            <span class="d-none d-sm-inline">{{ type.label }}</span>
          </small>
        </b-form-checkbox>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && items.length === 0" class="text-center py-5">
      <b-spinner label="Loading activities..."></b-spinner>
      <p class="mt-2 text-muted">{{ $t('global-activity-feed.loading') }}</p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="filteredItems.length === 0"
      class="text-center py-5 text-muted"
    >
      <b-icon-inbox font-scale="3" class="mb-2"></b-icon-inbox>
      <p>{{ $t('global-activity-feed.no-activities') }}</p>
    </div>

    <!-- Desktop table view -->
    <div
      v-else
      class="gid-global-feed__viewport gid-global-feed__desktop bg-white rounded border"
      ref="activityViewport"
    >
      <table class="w-100 mb-2">
        <colgroup>
          <col style="width: 15%" />
          <col style="width: 15%" />
          <col style="width: 15%" />
          <col style="width: 55%" />
        </colgroup>
        <template
          v-for="({ date, items: dateItems }, dateIndex) of itemsByDate"
        >
          <tr :key="`day-${dateIndex}`">
            <td colspan="4" class="gid-global-feed__heading pt-2">
              <h6
                class="font-weight-light border-bottom border-top bg-light rounded-top px-2 py-1 mb-0"
              >
                {{ date | moment('ll') }}
                <small class="text-muted">- {{ date | moment('from') }}</small>
              </h6>
            </td>
          </tr>
          <tr
            v-for="(item, itemIndex) of dateItems"
            :key="`day-${dateIndex}-item-${itemIndex}`"
            :class="{ 'border-bottom': itemIndex !== dateItems.length - 1 }"
            class="gid-global-feed__row"
          >
            <td class="px-2 gid-global-feed__col-time">
              <small
                class="d-inline-block bg-light border rounded-pill px-2 text-nowrap"
                :title="item.time | moment('LTS')"
                v-b-tooltip.hover
              >
                {{ item.time | moment('LT') }}
              </small>
            </td>
            <td class="pr-2 gid-global-feed__col-type">
              <small
                class="d-inline-block bg-light border rounded-pill px-2 text-nowrap"
              >
                <component :is="types[item.type].icon" variant="primary" />
                {{ types[item.type].label }}
              </small>
            </td>
            <td class="pr-2 gid-global-feed__col-job">
              <router-link
                v-if="item.value.jobLink"
                :to="item.value.jobLink"
                class="text-decoration-none"
              >
                <small class="text-primary text-truncate d-block">
                  {{ item.value.jobName || item.value.jobLink.params.job_id }}
                </small>
              </router-link>
              <small v-else class="text-muted">-</small>
            </td>
            <td class="pr-2 gid-global-feed__col-content">
              <component
                :is="types[item.type].component"
                :value="item.value"
                class="gid-global-feed__item"
                simple
              />
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Mobile card view -->
    <div v-if="filteredItems.length > 0" class="gid-global-feed__mobile">
      <template v-for="({ date, items: dateItems }, dateIndex) of itemsByDate">
        <div :key="`mobile-day-${dateIndex}`" class="mb-3">
          <h6
            class="font-weight-light bg-light rounded px-2 py-1 mb-2 border sticky-top"
          >
            {{ date | moment('ll') }}
            <small class="text-muted">- {{ date | moment('from') }}</small>
          </h6>
          <div
            v-for="(item, itemIndex) of dateItems"
            :key="`mobile-day-${dateIndex}-item-${itemIndex}`"
            class="gid-global-feed__card bg-white rounded border mb-2 p-3"
          >
            <!-- Row 1: Time -->
            <div class="mb-2">
              <small class="text-muted">Time:</small>
              <small class="bg-light border rounded-pill px-2 ml-2">
                {{ item.time | moment('LT') }}
              </small>
            </div>

            <!-- Row 2: Type -->
            <div class="mb-2">
              <small class="text-muted">Type:</small>
              <small class="bg-light border rounded-pill px-2 ml-2">
                <component :is="types[item.type].icon" variant="primary" />
                {{ types[item.type].label }}
              </small>
            </div>

            <!-- Row 3: Job -->
            <div class="mb-2">
              <small class="text-muted">Job:</small>
              <router-link
                v-if="item.value.jobLink"
                :to="item.value.jobLink"
                class="text-decoration-none ml-2"
              >
                <small class="text-primary">
                  {{ item.value.jobName || item.value.jobLink.params.job_id }}
                </small>
              </router-link>
              <small v-else class="text-muted ml-2">-</small>
            </div>

            <!-- Row 4: Content -->
            <div class="gid-global-feed__card-content border-top pt-2 mt-2">
              <component
                :is="types[item.type].component"
                :value="item.value"
                class="gid-global-feed__item"
                simple
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BIconChatLeftText,
  BIconSignpost,
  BIconStickies,
  BIconBoxArrowRight,
  BIconArrowClockwise,
  BIconInbox,
  BIconJournalText,
} from 'bootstrap-vue';
import axios from 'axios';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import { mapGetters } from 'vuex';

import { unwrapCrudList } from '../utils/crudList';

import CommentActivity from '@gid/vue-common/components/activity/CommentActivity.vue';
import StateActivity from '@gid/vue-common/components/activity/StateActivity.vue';
import CancellationActivity from '@gid/vue-common/components/activity/CancellationActivity.vue';
import TicketArticlesActivity from '@gid/vue-common/components/activity/TicketArticlesActivity.vue';
import SubmissionActivity from '@gid/vue-common/components/activity/SubmissionActivity.vue';
import LogActivity from '@gid/vue-common/components/activity/LogActivity.vue';

const ITEMS_LIMIT = 20;
const HOURS_LIMIT = 72;

function getTimeFilter() {
  const since = new Date();
  since.setHours(since.getHours() - HOURS_LIMIT);
  return since.toISOString();
}

async function fetchGlobalComments() {
  const query = RequestQueryBuilder.create({
    search: {
      createdAt: { [CondOperator.GREATER_THAN_EQUALS]: getTimeFilter() },
      // GID-2690: auto-generated system-event comments (S4C webhook payload
      // dumps, DKV events) would flood the global feed — exclude them here;
      // they stay reachable via the job activity feed's toggle.
      actionType: { [CondOperator.NOT_EQUALS]: 'system_event' },
    },
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: ITEMS_LIMIT,
  }).query();

  const { data } = await axios.get(`/data-api/comment?${query}`);

  return data.map((value) => ({
    time: new Date(value.createdAt),
    value: {
      ...value,
      _jobApiId: value.jobApiId || null,
      jobLink: null,
      jobName: null,
    },
  }));
}

async function fetchGlobalStates() {
  const query = RequestQueryBuilder.create({
    search: {
      createdAt: { [CondOperator.GREATER_THAN_EQUALS]: getTimeFilter() },
    },
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: ITEMS_LIMIT,
    join: [{ field: 'job' }],
  }).query();

  const { data } = await axios.get(`/data-api/job-state?${query}`);

  return data.map((value) => ({
    time: new Date(value.createdAt),
    value: {
      ...value,
      statuses: (value.statuses || []).map((status) => ({
        name: status,
        status: 'new',
      })),
      _jobId: value.job?.id || null,
      jobLink: null,
      jobName: null,
    },
  }));
}

async function fetchGlobalCancellations() {
  const query = RequestQueryBuilder.create({
    search: {
      createdAt: { [CondOperator.GREATER_THAN_EQUALS]: getTimeFilter() },
    },
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: ITEMS_LIMIT,
    join: [
      {
        field: 'cancellationReason.translations',
        select: ['locale', 'description'],
      },
      { field: 'opportunity' },
    ],
  }).query();

  const { data } = await axios.get(
    `/data-api/cancellation-reason-opportunity?${query}`,
  );

  return data.map((value) => ({
    time: new Date(value.createdAt),
    value: {
      definition: value,
      _oppId: value.opportunity?.id || null,
      jobLink: null,
      jobName: null,
    },
  }));
}

async function fetchGlobalTickets() {
  const query = RequestQueryBuilder.create({
    search: {
      createdAt: { [CondOperator.GREATER_THAN_EQUALS]: getTimeFilter() },
    },
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: ITEMS_LIMIT,
    // `messages` is no longer eager on the controller, and the feed renders the
    // latest message's subject and body, so join it explicitly.
    join: [{ field: 'jobs' }, { field: 'messages' }],
  }).query();

  const { data: body } = await axios.get(`/data-api/ticket?${query}`);
  // ITEMS_LIMIT is well under the controller's maxLimit, so this reads a single
  // page; unwrap defensively in case the endpoint starts always-paginating.
  const data = unwrapCrudList(body);

  const ticketItems = [];
  data.forEach((ticket) => {
    const latestMessage = ticket.messages?.[ticket.messages.length - 1];
    ticketItems.push({
      time: new Date(ticket.createdAt),
      value: {
        number: ticket.id,
        title: ticket.title,
        subject: latestMessage?.subject || ticket.title,
        content: latestMessage?.content || '',
        from: latestMessage?.from || '',
        to: latestMessage?.to || '',
        type: latestMessage?.type || '',
        _jobId: ticket.jobs?.[0]?.id || null,
        jobLink: ticket.jobs?.[0]?.name
          ? { name: 'jobs', params: { job_id: ticket.jobs[0].name } }
          : null,
        jobName: ticket.jobs?.[0]?.name || null,
      },
    });
  });

  return ticketItems;
}

async function fetchGlobalLogs() {
  const params = new URLSearchParams({
    startDate: getTimeFilter(),
    endDate: new Date().toISOString(),
    limit: String(ITEMS_LIMIT),
  });

  const { data } = await axios.get(
    `/data-api/entity/job/log-entries?${params}`,
  );

  return data.map((value) => ({
    time: new Date(value.time_stamp),
    value: {
      ...value,
      _jobApiId: value.job_api_id || null,
      jobLink: null,
      jobName: null,
    },
  }));
}

async function fetchGlobalSubmissions() {
  const query = RequestQueryBuilder.create({
    search: {
      createdAt: { [CondOperator.GREATER_THAN_EQUALS]: getTimeFilter() },
    },
    sort: [{ field: 'createdAt', order: 'DESC' }],
    limit: ITEMS_LIMIT,
  }).query();

  const { data } = await axios.get(`/data-api/entity/submission?${query}`);

  return data.map((value) => ({
    time: new Date(value.createdAt),
    value: {
      ...value,
      _jobId: value.jobId || null,
      jobLink: null,
      jobName: null,
    },
  }));
}

async function resolveJobNames(items) {
  const jobApiIds = new Set();
  const jobIds = new Set();
  const oppIds = new Set();

  items.forEach((item) => {
    if (!item.value.jobName) {
      if (item.value._jobApiId) jobApiIds.add(item.value._jobApiId);
      if (item.value._jobId) jobIds.add(item.value._jobId);
      if (item.value._oppId) oppIds.add(item.value._oppId);
    }
  });

  if (!jobApiIds.size && !jobIds.size && !oppIds.size) return;

  const conditions = [];
  if (jobApiIds.size) {
    conditions.push({ apiId: { [CondOperator.IN]: [...jobApiIds] } });
  }
  if (jobIds.size) {
    conditions.push({ id: { [CondOperator.IN]: [...jobIds] } });
  }
  if (oppIds.size) {
    conditions.push({
      opportunityId: { [CondOperator.IN]: [...oppIds] },
    });
  }

  const search = conditions.length === 1 ? conditions[0] : { $or: conditions };
  const query = RequestQueryBuilder.create({
    search,
    fields: ['id', 'name', 'apiId', 'opportunityId'],
    limit: 200,
  }).query();

  try {
    const { data } = await axios.get(`/data-api/entity/job?${query}`);

    const byApiId = {};
    const byId = {};
    const byOppId = {};
    data.forEach((job) => {
      if (job.apiId) byApiId[job.apiId] = job.name;
      if (job.id) byId[job.id] = job.name;
      if (job.opportunityId) byOppId[job.opportunityId] = job.name;
    });

    items.forEach((item) => {
      if (item.value.jobName) return;
      const name =
        byApiId[item.value._jobApiId] ||
        byId[item.value._jobId] ||
        byOppId[item.value._oppId];
      if (name) {
        item.value.jobName = name;
        item.value.jobLink = { name: 'jobs', params: { job_id: name } };
      }
    });
  } catch (error) {
    console.error('Error resolving job names:', error);
  }
}

export default {
  components: {
    BIconChatLeftText,
    BIconSignpost,
    BIconStickies,
    BIconBoxArrowRight,
    BIconArrowClockwise,
    BIconInbox,
    BIconJournalText,
    CommentActivity,
    StateActivity,
    CancellationActivity,
    TicketArticlesActivity,
    SubmissionActivity,
    LogActivity,
  },
  data() {
    return {
      loading: false,
      items: [],
      types: {
        state: {
          icon: 'BIconSignpost',
          label: this.$t('activity-feed.type.state'),
          component: 'StateActivity',
          fetch: fetchGlobalStates,
          enabled: true,
          loading: false,
        },
        comment: {
          icon: 'BIconChatLeftText',
          label: this.$t('activity-feed.type.comment'),
          component: 'CommentActivity',
          fetch: fetchGlobalComments,
          enabled: true,
          loading: false,
        },
        log: {
          icon: 'BIconJournalText',
          label: this.$t('activity-feed.type.log'),
          component: 'LogActivity',
          fetch: fetchGlobalLogs,
          enabled: true,
          loading: false,
        },
        cr: {
          icon: 'BIconBoxArrowRight',
          label: this.$t('activity-feed.type.cr'),
          component: 'CancellationActivity',
          fetch: fetchGlobalCancellations,
          enabled: true,
          loading: false,
        },
        ticketArticles: {
          icon: 'BIconStickies',
          label: this.$t('activity-feed.type.ticket'),
          component: 'TicketArticlesActivity',
          fetch: fetchGlobalTickets,
          enabled: true,
          loading: false,
        },
        submission: {
          icon: 'BIconStickies',
          label: this.$t('activity-feed.type.submission'),
          component: 'SubmissionActivity',
          fetch: fetchGlobalSubmissions,
          enabled: true,
          loading: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters(['locale']),
    filteredItems() {
      return this.items
        .filter((item) => this.types[item.type]?.enabled)
        .slice(0, ITEMS_LIMIT);
    },
    itemsByDate() {
      const grouped = {};
      this.filteredItems.forEach((item) => {
        const dateKey = new Date(item.time).toDateString();
        if (!grouped[dateKey]) {
          grouped[dateKey] = {
            date: new Date(item.time),
            items: [],
          };
        }
        grouped[dateKey].items.push(item);
      });

      return Object.values(grouped).sort((a, b) => b.date - a.date);
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    checkOnly(type) {
      if (!this.types[type].loading) {
        Object.keys(this.types).forEach((key) => {
          this.types[key].enabled = key === type;
        });
      }
    },
    async loadData() {
      this.loading = true;
      this.items = [];

      const fetchPromises = Object.entries(this.types).map(
        async ([type, typeDef]) => {
          this.$set(typeDef, 'loading', true);
          try {
            const typeItems = await typeDef.fetch();
            return typeItems.map((item) => ({ ...item, type }));
          } catch (error) {
            console.error(`Error loading ${type} activities:`, error);
            return [];
          } finally {
            typeDef.loading = false;
          }
        },
      );

      const results = await Promise.all(fetchPromises);
      const allItems = results
        .flat()
        .sort((a, b) => b.time - a.time)
        .slice(0, ITEMS_LIMIT * 2);

      await resolveJobNames(allItems);
      this.items = allItems;

      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
$breakpoint-desktop: 1280px;

.gid-global-feed {
  width: 100%;

  // Desktop table view
  &__viewport {
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 600px;
    resize: vertical;
    scroll-behavior: smooth;
    width: 100%;

    > *:first-child {
      margin-top: calc(-0.5rem - 2px);
    }

    table {
      table-layout: fixed;
      width: 100%;
    }
  }

  // Show desktop view only at >= 1280px
  &__desktop {
    display: none;

    @media (min-width: $breakpoint-desktop) {
      display: block;
    }
  }

  // Show mobile view only at < 1280px
  &__mobile {
    display: block;
    max-height: 600px;
    overflow-y: auto;
    overflow-x: hidden;

    @media (min-width: $breakpoint-desktop) {
      display: none;
    }

    .sticky-top {
      background-color: #f8f9fa;
      z-index: 5;
    }
  }

  &__col-job {
    overflow: hidden;
  }

  &__heading {
    position: sticky;
    top: calc(-0.5rem - 2px);
    z-index: 6;
  }

  &__row:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &__item {
    overflow: hidden;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .table & td {
    padding: 0 0 0.2rem;
    border: none;
  }

  .table-striped tbody & tr:nth-of-type(odd) {
    background: none;
  }

  @media (hover: hover) {
    &__item:not(:hover) &__details {
      display: none;
    }
  }

  &__card {
    &:hover {
      background-color: rgba(0, 0, 0, 0.02) !important;
    }
  }

  &__card-content {
    font-size: 0.9rem;
  }

  // Filter checkboxes on mobile - show only icons
  &__filter {
    .custom-control-label {
      padding-left: 0.25rem;
    }
  }
}
</style>
